import React, { useEffect, useState, useRef } from 'react';
import { connect } from 'react-redux';
import PageTitle from '../../Component/Comman/PageTitle';
import { fetchBankAccounts, addBankAccount, updateBankAccount } from '../../Redux/Actions/dataActions';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';

function BankAccountPage({ bankAccounts, fetchBankAccounts, addBankAccount, updateBankAccount, loading }) {
    const hasFetched = useRef(false);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingAccount, setEditingAccount] = useState(null);
    const [formData, setFormData] = useState({
        accountHolderName: '',
        bankName: '',
        accountNumber: '',
        ifscCode: '',
        walletAddress: '',
        walletName: '',
        networkType: '',
        isPrimary: false
    });
    const [formErrors, setFormErrors] = useState({});
    const [submitError, setSubmitError] = useState(null);
    const [submitSuccess, setSubmitSuccess] = useState(false);

    useEffect(() => {
        // Only fetch once when component mounts
        if (!hasFetched.current && !loading) {
            hasFetched.current = true;
            fetchBankAccounts();
        }
    }, [fetchBankAccounts, loading]);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        // Clear error for this field
        if (formErrors[name]) {
            setFormErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const validateForm = () => {
        const errors = {};
        
        if (!formData.accountHolderName.trim()) {
            errors.accountHolderName = 'Account holder name is required';
        }

        // Bank account fields are always required
        if (!formData.bankName.trim()) {
            errors.bankName = 'Bank name is required';
        }
        if (!formData.accountNumber.trim() || formData.accountNumber === 'N/A') {
            errors.accountNumber = 'Account number is required';
        }
        if (!formData.ifscCode.trim() || formData.ifscCode === 'N/A') {
            errors.ifscCode = 'IFSC code is required';
        }

        // Crypto wallet fields are optional, but if one is filled, all should be filled
        const hasCryptoFields = formData.walletAddress.trim() || formData.walletName.trim() || formData.networkType.trim();
        if (hasCryptoFields) {
            if (!formData.walletAddress.trim()) {
                errors.walletAddress = 'Wallet address is required when adding crypto wallet';
            }
            if (!formData.walletName.trim()) {
                errors.walletName = 'Wallet name is required when adding crypto wallet';
            }
            if (!formData.networkType.trim()) {
                errors.networkType = 'Network type is required when adding crypto wallet';
            }
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const prepareFormData = () => {
        const data = {
            accountHolderName: formData.accountHolderName,
            bankName: formData.bankName,
            accountNumber: formData.accountNumber,
            ifscCode: formData.ifscCode,
            isPrimary: formData.isPrimary
        };

        // Only include crypto fields if they are filled
        if (formData.walletAddress.trim() && formData.walletName.trim() && formData.networkType.trim()) {
            data.walletAddress = formData.walletAddress;
            data.walletName = formData.walletName;
            data.networkType = formData.networkType;
        }

        return data;
    };

    const handleAddAccount = async (e) => {
        e.preventDefault();
        setSubmitError(null);
        setSubmitSuccess(false);

        if (!validateForm()) {
            return;
        }

        try {
            const dataToSubmit = prepareFormData();
            const result = await addBankAccount(dataToSubmit);
            if (result.success) {
                setSubmitSuccess(true);
                setFormData({
                    accountHolderName: '',
                    bankName: '',
                    accountNumber: '',
                    ifscCode: '',
                    walletAddress: '',
                    walletName: '',
                    networkType: '',
                    isPrimary: false
                });
                setTimeout(() => {
                    setShowAddModal(false);
                    setSubmitSuccess(false);
                }, 1500);
            } else {
                setSubmitError(result.error || result.message || 'Failed to add bank account');
            }
        } catch (error) {
            setSubmitError(error.message || 'Failed to add bank account');
        }
    };

    const handleEditClick = (account) => {
        setEditingAccount(account);
        const cryptoDetails = account.cryptoDetails || {};

        setFormData({
            accountHolderName: account.accountHolderName || '',
            bankName: account.bankName || '',
            accountNumber: account.accountNumber || '',
            ifscCode: account.ifscCode || '',
            walletAddress: cryptoDetails.walletAddress || '',
            walletName: cryptoDetails.walletName || '',
            networkType: cryptoDetails.networkType || '',
            isPrimary: account.isPrimary || false
        });
        setFormErrors({});
        setSubmitError(null);
        setSubmitSuccess(false);
        setShowEditModal(true);
    };

    const handleUpdateAccount = async (e) => {
        e.preventDefault();
        setSubmitError(null);
        setSubmitSuccess(false);

        if (!validateForm()) {
            return;
        }

        try {
            const accountId = editingAccount._id || editingAccount.id;
            const dataToSubmit = prepareFormData();
            const result = await updateBankAccount(accountId, dataToSubmit);
            if (result.success) {
                setSubmitSuccess(true);
                setTimeout(() => {
                    setShowEditModal(false);
                    setEditingAccount(null);
                    setSubmitSuccess(false);
                }, 1500);
            } else {
                setSubmitError(result.error || result.message || 'Failed to update bank account');
            }
        } catch (error) {
            setSubmitError(error.message || 'Failed to update bank account');
        }
    };

    const handleCloseAddModal = () => {
        setShowAddModal(false);
        setFormData({
            accountHolderName: '',
            bankName: '',
            accountNumber: '',
            ifscCode: '',
            walletAddress: '',
            walletName: '',
            networkType: 'ERC-20',
            isPrimary: false
        });
        setFormErrors({});
        setSubmitError(null);
        setSubmitSuccess(false);
    };

    const handleCloseEditModal = () => {
        setShowEditModal(false);
        setEditingAccount(null);
        setFormData({
            accountHolderName: '',
            bankName: '',
            accountNumber: '',
            ifscCode: '',
            walletAddress: '',
            walletName: '',
            networkType: 'ERC-20',
            isPrimary: false
        });
        setFormErrors({});
        setSubmitError(null);
        setSubmitSuccess(false);
    };

    const accountList = Array.isArray(bankAccounts) ? bankAccounts : (bankAccounts?.data || []);

    return (
        <>
            <div className="body-header border-bottom d-flex py-3 mb-3">
                <PageTitle pagetitle='Bank Accounts' sidebutton={true} />
            </div>
            <div className="container-xxl">
                <div className="row mb-3">
                    <div className="col-12">
                        <button 
                            className="btn btn-primary" 
                            onClick={() => setShowAddModal(true)}
                            disabled={loading}
                        >
                            <i className="icofont-plus me-2"></i>Add Bank Account
                        </button>
                    </div>
                </div>

                {loading && (!accountList || accountList.length === 0) ? (
                    <div className="card">
                        <div className="card-body">
                            <div className="text-center py-4">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>
                        </div>
                    </div>
                ) : accountList && accountList.length > 0 ? (
                    <div className="row g-3">
                        {accountList.map((account, index) => {
                            const hasCrypto = account.hasCryptoWallet || account.cryptoDetails;
                            const cryptoDetails = account.cryptoDetails || {};
                            
                            return (
                                <div key={account._id || account.id || index} className="col-md-6 col-lg-4">
                                    <div className={`card h-100 ${account.isPrimary ? 'border-primary' : ''}`}>
                                        <div className="card-header py-3 bg-transparent border-bottom d-flex justify-content-between align-items-center">
                                            <div className="d-flex align-items-center gap-2">
                                                <h6 className="mb-0 fw-bold">{account.bankName || 'Bank Account'}</h6>
                                                {account.isPrimary && (
                                                    <span className="badge bg-primary">Primary</span>
                                                )}
                                                {account.isVerified && (
                                                    <span className="badge bg-success">Verified</span>
                                                )}
                                            </div>
                                            <button
                                                className="btn btn-sm btn-outline-primary"
                                                onClick={() => handleEditClick(account)}
                                                title="Edit"
                                            >
                                                <i className="icofont-edit"></i>
                                            </button>
                                        </div>
                                        <div className="card-body">
                                            <div className="mb-2">
                                                <small className="text-muted d-block">Account Holder</small>
                                                <div className="fw-semibold">{account.accountHolderName || 'N/A'}</div>
                                            </div>
                                            {account.accountNumber && account.accountNumber !== 'N/A' && (
                                                <>
                                                    <div className="mb-2">
                                                        <small className="text-muted d-block">Account Number</small>
                                                        <div className="fw-semibold">{account.accountNumber}</div>
                                                    </div>
                                                    {account.ifscCode && account.ifscCode !== 'N/A' && (
                                                        <div className="mb-2">
                                                            <small className="text-muted d-block">IFSC Code</small>
                                                            <div>{account.ifscCode}</div>
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                            {hasCrypto && (
                                                <div className="mt-3 pt-3 border-top">
                                                    <small className="text-muted d-block mb-2">Crypto Wallet</small>
                                                    {cryptoDetails.walletName && (
                                                        <div className="mb-2">
                                                            <small className="text-muted d-block">Wallet Name</small>
                                                            <div className="fw-semibold">{cryptoDetails.walletName}</div>
                                                        </div>
                                                    )}
                                                    {cryptoDetails.walletAddress && (
                                                        <div className="mb-2">
                                                            <small className="text-muted d-block">Wallet Address</small>
                                                            <div className="text-break small">{cryptoDetails.walletAddress}</div>
                                                        </div>
                                                    )}
                                                    {cryptoDetails.networkType && (
                                                        <div className="mb-2">
                                                            <small className="text-muted d-block">Network</small>
                                                            <span className="badge bg-info">{cryptoDetails.networkType}</span>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                            {account.status && (
                                                <div className="mt-2">
                                                    <span className={`badge ${
                                                        account.status === 'active' ? 'bg-success' : 
                                                        account.status === 'inactive' ? 'bg-warning' : 
                                                        'bg-secondary'
                                                    }`}>
                                                        {account.status.charAt(0).toUpperCase() + account.status.slice(1)}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div className="card">
                        <div className="card-body">
                            <div className="text-center py-5">
                                <i className="icofont-bank fs-1 text-muted mb-3 d-block"></i>
                                <p className="text-muted mb-0">No bank accounts added yet. Click "Add Bank Account" to get started.</p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Add Bank Account Modal */}
                <Modal show={showAddModal} onHide={handleCloseAddModal} size="lg">
                    <Modal.Header closeButton>
                        <Modal.Title>Add Bank Account</Modal.Title>
                    </Modal.Header>
                    <form onSubmit={handleAddAccount}>
                        <Modal.Body>
                            {submitSuccess && (
                                <div className="alert alert-success">
                                    <i className="icofont-check-circled me-2"></i>Bank account added successfully!
                                </div>
                            )}
                            {submitError && (
                                <div className="alert alert-danger">
                                    <i className="icofont-close-circled me-2"></i>{submitError}
                                </div>
                            )}

                            <div className="row g-3">
                                <div className="col-md-12">
                                    <label className="form-label">Account Holder Name <span className="text-danger">*</span></label>
                                    <input
                                        type="text"
                                        className={`form-control ${formErrors.accountHolderName ? 'is-invalid' : ''}`}
                                        name="accountHolderName"
                                        value={formData.accountHolderName}
                                        onChange={handleInputChange}
                                        required
                                    />
                                    {formErrors.accountHolderName && (
                                        <div className="invalid-feedback">{formErrors.accountHolderName}</div>
                                    )}
                                </div>

                                <div className="col-md-6">
                                    <label className="form-label">Bank Name <span className="text-danger">*</span></label>
                                    <input
                                        type="text"
                                        className={`form-control ${formErrors.bankName ? 'is-invalid' : ''}`}
                                        name="bankName"
                                        value={formData.bankName}
                                        onChange={handleInputChange}
                                        required
                                    />
                                    {formErrors.bankName && (
                                        <div className="invalid-feedback">{formErrors.bankName}</div>
                                    )}
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label">Account Number <span className="text-danger">*</span></label>
                                    <input
                                        type="text"
                                        className={`form-control ${formErrors.accountNumber ? 'is-invalid' : ''}`}
                                        name="accountNumber"
                                        value={formData.accountNumber}
                                        onChange={handleInputChange}
                                        required
                                    />
                                    {formErrors.accountNumber && (
                                        <div className="invalid-feedback">{formErrors.accountNumber}</div>
                                    )}
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label">IFSC Code <span className="text-danger">*</span></label>
                                    <input
                                        type="text"
                                        className={`form-control ${formErrors.ifscCode ? 'is-invalid' : ''}`}
                                        name="ifscCode"
                                        value={formData.ifscCode}
                                        onChange={handleInputChange}
                                        required
                                    />
                                    {formErrors.ifscCode && (
                                        <div className="invalid-feedback">{formErrors.ifscCode}</div>
                                    )}
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label">Wallet Address</label>
                                    <input
                                        type="text"
                                        className={`form-control ${formErrors.walletAddress ? 'is-invalid' : ''}`}
                                        name="walletAddress"
                                        value={formData.walletAddress}
                                        onChange={handleInputChange}
                                    />
                                    {formErrors.walletAddress && (
                                        <div className="invalid-feedback">{formErrors.walletAddress}</div>
                                    )}
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label">Wallet Name</label>
                                    <input
                                        type="text"
                                        className={`form-control ${formErrors.walletName ? 'is-invalid' : ''}`}
                                        name="walletName"
                                        value={formData.walletName}
                                        onChange={handleInputChange}
                                    />
                                    {formErrors.walletName && (
                                        <div className="invalid-feedback">{formErrors.walletName}</div>
                                    )}
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label">Network Type</label>
                                    <select
                                        className={`form-control ${formErrors.networkType ? 'is-invalid' : ''}`}
                                        name="networkType"
                                        value={formData.networkType}
                                        onChange={handleInputChange}
                                    >
                                        <option value="">Select Network Type</option>
                                        <option value="ERC-20">ERC-20</option>
                                        <option value="TRC-20">TRC-20</option>
                                        <option value="BEP-20">BEP-20</option>
                                        <option value="Other">Other</option>
                                    </select>
                                    {formErrors.networkType && (
                                        <div className="invalid-feedback">{formErrors.networkType}</div>
                                    )}
                                </div>

                                <div className="col-md-12">
                                    <div className="form-check">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            name="isPrimary"
                                            id="isPrimary"
                                            checked={formData.isPrimary}
                                            onChange={handleInputChange}
                                        />
                                        <label className="form-check-label" htmlFor="isPrimary">
                                            Set as Primary Account
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={handleCloseAddModal} disabled={loading}>
                                Cancel
                            </Button>
                            <Button variant="primary" type="submit" disabled={loading}>
                                {loading ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                        Adding...
                                    </>
                                ) : (
                                    'Add Bank Account'
                                )}
                            </Button>
                        </Modal.Footer>
                    </form>
                </Modal>

                {/* Edit Bank Account Modal */}
                <Modal show={showEditModal} onHide={handleCloseEditModal} size="lg">
                    <Modal.Header closeButton>
                        <Modal.Title>Edit Bank Account</Modal.Title>
                    </Modal.Header>
                    <form onSubmit={handleUpdateAccount}>
                        <Modal.Body>
                            {submitSuccess && (
                                <div className="alert alert-success">
                                    <i className="icofont-check-circled me-2"></i>Bank account updated successfully!
                                </div>
                            )}
                            {submitError && (
                                <div className="alert alert-danger">
                                    <i className="icofont-close-circled me-2"></i>{submitError}
                                </div>
                            )}

                            <div className="row g-3">
                                <div className="col-md-12">
                                    <label className="form-label">Account Holder Name <span className="text-danger">*</span></label>
                                    <input
                                        type="text"
                                        className={`form-control ${formErrors.accountHolderName ? 'is-invalid' : ''}`}
                                        name="accountHolderName"
                                        value={formData.accountHolderName}
                                        onChange={handleInputChange}
                                        required
                                    />
                                    {formErrors.accountHolderName && (
                                        <div className="invalid-feedback">{formErrors.accountHolderName}</div>
                                    )}
                                </div>

                                <div className="col-md-6">
                                    <label className="form-label">Bank Name <span className="text-danger">*</span></label>
                                    <input
                                        type="text"
                                        className={`form-control ${formErrors.bankName ? 'is-invalid' : ''}`}
                                        name="bankName"
                                        value={formData.bankName}
                                        onChange={handleInputChange}
                                        required
                                    />
                                    {formErrors.bankName && (
                                        <div className="invalid-feedback">{formErrors.bankName}</div>
                                    )}
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label">Account Number <span className="text-danger">*</span></label>
                                    <input
                                        type="text"
                                        className={`form-control ${formErrors.accountNumber ? 'is-invalid' : ''}`}
                                        name="accountNumber"
                                        value={formData.accountNumber}
                                        onChange={handleInputChange}
                                        required
                                    />
                                    {formErrors.accountNumber && (
                                        <div className="invalid-feedback">{formErrors.accountNumber}</div>
                                    )}
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label">IFSC Code <span className="text-danger">*</span></label>
                                    <input
                                        type="text"
                                        className={`form-control ${formErrors.ifscCode ? 'is-invalid' : ''}`}
                                        name="ifscCode"
                                        value={formData.ifscCode}
                                        onChange={handleInputChange}
                                        required
                                    />
                                    {formErrors.ifscCode && (
                                        <div className="invalid-feedback">{formErrors.ifscCode}</div>
                                    )}
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label">Wallet Address</label>
                                    <input
                                        type="text"
                                        className={`form-control ${formErrors.walletAddress ? 'is-invalid' : ''}`}
                                        name="walletAddress"
                                        value={formData.walletAddress}
                                        onChange={handleInputChange}
                                    />
                                    {formErrors.walletAddress && (
                                        <div className="invalid-feedback">{formErrors.walletAddress}</div>
                                    )}
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label">Wallet Name</label>
                                    <input
                                        type="text"
                                        className={`form-control ${formErrors.walletName ? 'is-invalid' : ''}`}
                                        name="walletName"
                                        value={formData.walletName}
                                        onChange={handleInputChange}
                                    />
                                    {formErrors.walletName && (
                                        <div className="invalid-feedback">{formErrors.walletName}</div>
                                    )}
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label">Network Type</label>
                                    <select
                                        className={`form-control ${formErrors.networkType ? 'is-invalid' : ''}`}
                                        name="networkType"
                                        value={formData.networkType}
                                        onChange={handleInputChange}
                                    >
                                        <option value="">Select Network Type</option>
                                        <option value="ERC-20">ERC-20</option>
                                        <option value="TRC-20">TRC-20</option>
                                        <option value="BEP-20">BEP-20</option>
                                        <option value="Other">Other</option>
                                    </select>
                                    {formErrors.networkType && (
                                        <div className="invalid-feedback">{formErrors.networkType}</div>
                                    )}
                                </div>

                                <div className="col-md-12">
                                    <div className="form-check">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            name="isPrimary"
                                            id="editIsPrimary"
                                            checked={formData.isPrimary}
                                            onChange={handleInputChange}
                                        />
                                        <label className="form-check-label" htmlFor="editIsPrimary">
                                            Set as Primary Account
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={handleCloseEditModal} disabled={loading}>
                                Cancel
                            </Button>
                            <Button variant="primary" type="submit" disabled={loading}>
                                {loading ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                        Updating...
                                    </>
                                ) : (
                                    'Update Bank Account'
                                )}
                            </Button>
                        </Modal.Footer>
                    </form>
                </Modal>
            </div>
        </>
    );
}

const mapStateToProps = (state) => ({
    bankAccounts: state.data?.bankAccounts,
    loading: state.data?.loading?.bankAccounts || false,
});

export default connect(mapStateToProps, {
    fetchBankAccounts,
    addBankAccount,
    updateBankAccount
})(BankAccountPage);
